/**
 * 统一登录入口 —— 卖家 / 货代 / 平台运营三端共用，接的是**真实后端**（2026-08-06 实测通过）。
 *
 * <h2>🔴 主体类型由**路径**决定：`POST /auth/v1/{tenant|forwarder|operator}/login`</h2>
 * 后端 `AuthService.login()` 的注释原话：
 * 「它来自**路径**而不是请求体：请求体里的主体类型等于让调用方自选身份」。
 * ⇒ 角色是**选出来的路径**，⛔ 绝不作为请求体字段传给后端。
 *
 * <p>⚠️ 「用户能在页面上选运营」**不等于**「他能进运营端」：
 * 后端 `resolveIdentity` 会检查这个账号在该入口下**确实有启用的身份**，
 * 没有就是 `99405`。选择器只决定问哪扇门，开不开门是后端说了算。
 *
 * <h2>🔴🔴 为什么这里自己读信封，不用 shared 的 http 客户端</h2>
 * shared 的客户端在 `code !== 0` 时抛 `ApiError`，**丢掉 `data`**。
 * 而 `99404`（该账号有多个身份）是**唯一一个带 data 的失败响应** ——
 * 那个 `data.identities` 正是要给用户挑的列表。
 * ⇒ 用通用客户端的话，多身份账号会看到一句「登录失败」然后**无路可走**。
 *
 * <h2>⚠️ 一个账号可能同时是卖家和货代</h2>
 * `user_identity` 表注释原文：「一人多身份（**同一手机号可同时是商家运营和货代库管**）」。
 * ⇒ 在**卖家入口**里，`99404` 的候选只会是多个 TENANT 身份（多家店/多个主体）；
 *   若这个人还有货代身份，后端会把它挡在这个入口外（`99405`）—— 那是刻意的隔离。
 */

import { normalizeAuthId, type AuthId, type WireAuthId } from '@erp/shared'
import { bearerHeader, saveTokens, type SellerSession } from './tokens'

/** 后端统一信封。🔴 业务失败是 HTTP 200 + code≠0，⛔ 不能只看 response.ok */
interface Envelope<T> {
  code: number
  message: string
  data: T | null
  traceId: string | null
}

export interface IdentityOption {
  identityId: string
  subjectType: string
  subjectId: AuthId
  /** ⚠️ 后端契约明说**可能为 null**，界面要兜住 */
  displayName: string | null
}

interface TokenPair {
  accessToken: string
  accessExpiresIn: number
  refreshToken: string
  refreshExpiresIn: number
  userId: AuthId
  subjectType: string
  tenantId: AuthId
  subjectId: AuthId
  mustChangePassword: boolean
}

type TokenPairWire = Omit<TokenPair, 'userId' | 'tenantId' | 'subjectId'> & {
  userId: WireAuthId
  tenantId: WireAuthId
  subjectId: WireAuthId
}

type IdentityOptionWire = Omit<IdentityOption, 'identityId' | 'subjectId'> & {
  identityId: WireAuthId
  subjectId: WireAuthId
}

function tokenPairOf(pair: TokenPairWire): TokenPair {
  return {
    ...pair,
    userId: normalizeAuthId(pair.userId, 'userId'),
    tenantId: normalizeAuthId(pair.tenantId, 'tenantId'),
    subjectId: normalizeAuthId(pair.subjectId, 'subjectId'),
  }
}

function identityOptionOf(option: IdentityOptionWire): IdentityOption {
  const identityId = normalizeAuthId(option.identityId, 'identityId')
  if (identityId === null) throw new TypeError('identityId must not be null')
  return {
    ...option,
    identityId,
    subjectId: normalizeAuthId(option.subjectId, 'subjectId'),
  }
}

export type LoginOutcome =
  | {
      kind: 'session'
      session: SellerSession
      /**
       * 🔴 原始令牌对 —— 只有 **FORWARDER** 需要它（要交接给货代端应用，
       * 见 tokens.ts 的 `handOffToForwarder`）。
       * ⚠️ TENANT / OPERATOR 的令牌已经在 `login()` 里存好了，⛔ 不要再存一次。
       */
      raw: {
        accessToken: string
        accessExpiresIn: number
        refreshToken: string
        refreshExpiresIn: number
      }
    }
  /** 🔴 口令已经验过了，只差挑一个身份 —— 带上 identityId 再发一次 */
  | { kind: 'choose-identity'; identities: IdentityOption[] }

/** 登录失败。🔴 带上 `code`，因为界面要按码决定**动作**（而不是另写文案） */
export class LoginError extends Error {
  constructor(
    readonly code: number,
    message: string,
    /**
     * 🔴 重试永远不会成功 ⇒ 界面据此**隐藏重试入口**。
     * ⚠️ 默认 false —— 拿不准时允许再试一次，是比拦住他更小的伤害。
     */
    readonly terminal: boolean,
  ) {
    super(message)
    this.name = 'LoginError'
  }
}

/** 卖家注册请求。公开注册只创建 TENANT；货代需要审核，走独立入驻流程。 */
export interface TenantRegisterRequest {
  companyName: string
  contactPhone: string
  password: string
  /** 可选推荐人/客户经理编号；只做归因，不是注册准入凭据。 */
  referrerId?: string
  registerSource?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  vid?: string
  /** 手机验证码验证成功后签发；仅允许用于紧随其后的一次注册请求。 */
  verificationTicket: string
}

/** 注册成功只返回标识，不返回令牌；用户仍需走一次正常登录。 */
export interface TenantRegisterResult {
  tenantId: string
  userId: string
}

export interface ForwarderQualification {
  licenseNo: string
  contactName: string
  warehouseAddress: string
  warehouseCity: string
  canInvoice: boolean
  settlePeriod: 'MONTHLY'
}

export function registerForwarder(request: TenantRegisterRequest & { qualification: ForwarderQualification }): Promise<TenantRegisterResult> {
  return registerAccount(request, 'FORWARDER', request.qualification)
}

type TenantRegisterResultWire = {
  tenantId: WireAuthId
  userId: WireAuthId
}

export class RegistrationError extends Error {
  constructor(readonly code: number, message: string) {
    super(message)
    this.name = 'RegistrationError'
  }
}

/** 这些码重试没有意义：处置是去找人，不是再点一次 */
const TERMINAL_CODES = new Set([
  99403, // 账号已停用
  99405, // 该账号无法从此入口登录
  99408, // 登录服务尚未配置
])

async function postEnvelope<T>(
  path: string,
  body: unknown,
  headers: Record<string, string> = {},
): Promise<Envelope<T>> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 15_000)
  let res: Response
  try {
    res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(body),
      signal: controller.signal,
    })
  } catch (e) {
    // 🔴 「请求没到后端」与「后端给了判定」必须分开：前者可以重试，后者多半不行
    throw new LoginError(
      -1,
      controller.signal.aborted ? '请求超时，请检查网络后重试。' : '无法连接服务器，请检查网络。',
      false,
    )
  } finally {
    clearTimeout(timer)
  }

  let json: Envelope<T>
  try {
    json = (await res.json()) as Envelope<T>
  } catch {
    // ⚠️ 非 JSON 响应最常见的成因是**代理没配** —— 请求被前端路由接走，返回了 index.html。
    //    这个提示是刻意具体的：泛泛说「服务异常」会让人往后端方向查一整天。
    throw new LoginError(-1, `服务器返回了非预期内容（HTTP ${res.status}）。`, false)
  }
  return json
}

export interface RegistrationVerificationTicket {
  verificationTicket: string
  expiresInSeconds: number
}

async function registrationEnvelope<T>(path: string, body: unknown): Promise<T | null> {
  try {
    const env = await postEnvelope<T>(path, body)
    if (env.code !== 0) throw new RegistrationError(env.code, env.message)
    return env.data
  } catch (e) {
    if (e instanceof RegistrationError) throw e
    if (e instanceof LoginError) throw new RegistrationError(e.code, e.message)
    throw e
  }
}

/** 公开发码始终使用中性响应，不向调用方透露手机号是否已经注册。 */
export async function sendRegistrationSmsCode(phone: string): Promise<void> {
  await registrationEnvelope('/auth/v1/registration/sms-code', { phone })
}

/** 验证验证码并换取短期一次性注册票据。调用方不得持久化该票据。 */
export async function verifyRegistrationSmsCode(
  phone: string,
  code: string,
): Promise<RegistrationVerificationTicket> {
  const data = await registrationEnvelope<RegistrationVerificationTicket>(
    '/auth/v1/registration/sms-code/verify',
    { phone, code },
  )
  if (!data?.verificationTicket) throw new RegistrationError(-1, '手机号验证服务暂不可用')
  return data
}

/**
 * 注册卖家租户。subjectType 在这里固定为 TENANT，避免公开页面让调用方自选高风险主体。
 * 注册成功不会自动登录，这是后端契约的一部分。
 */
export async function registerTenant(request: TenantRegisterRequest): Promise<TenantRegisterResult> {
  return registerAccount(request, 'TENANT')
}

async function registerAccount(request: TenantRegisterRequest, subjectType: 'TENANT' | 'FORWARDER', qualification?: ForwarderQualification): Promise<TenantRegisterResult> {
  try {
    const env = await postEnvelope<TenantRegisterResultWire>('/auth/v1/register', {
      subjectType,
      ...(qualification ? { qualification } : {}),
      companyName: request.companyName,
      contactPhone: request.contactPhone,
      password: request.password,
      referrerId: request.referrerId ?? null,
      registerSource: request.registerSource ?? null,
      utmSource: request.utmSource ?? null,
      utmMedium: request.utmMedium ?? null,
      utmCampaign: request.utmCampaign ?? null,
      vid: request.vid ?? null,
      verificationTicket: request.verificationTicket,
    })
    if (env.code !== 0 || !env.data) {
      throw new RegistrationError(env.code, env.message || '注册失败，请稍后重试')
    }
    const tenantId = normalizeAuthId(env.data.tenantId, 'tenantId')
    const userId = normalizeAuthId(env.data.userId, 'userId')
    if (tenantId === null || userId === null) throw new RegistrationError(-1, '注册结果缺少账号标识')
    return { tenantId, userId }
  } catch (e) {
    if (e instanceof RegistrationError) throw e
    if (e instanceof LoginError) throw new RegistrationError(e.code, e.message)
    throw e
  }
}

/**
 * 登录角色 —— 🔴 它决定**打哪条路径**，而路径决定后端认定的主体类型。
 *
 * <h2>🔴🔴 为什么是「让用户先选角色」，而不是「后端自己判断」</h2>
 * 另一条看起来更聪明的路是：加一个不带主体的登录端点，由后端把这个账号
 * 名下**所有**身份都返回，前端再让用户挑。它有一个**会真出事**的前提问题：
 *
 * <p>如果退而求其次由前端「先试卖家、失败再试货代」——
 * `AuthService` 在口令校验失败时会 `directory.recordFailure(...)` 计数，
 * 达到阈值就锁号。⇒ **一个只有货代身份的人，每次登录都会白记一次失败**，
 * 几次之后他的账号自己就锁了，而他什么都没做错。
 *
 * <p>⇒ 选角色 = **一次登录、一条路径**，
 * 同时 `resolveIdentity` 里那道「身份必须属于本入口」的校验（`99405`）原样保留。
 *
 * <p>⚠️ 它**不增加攻击面**：三个入口本来就都存在，选择器只是把它们画在一个页面上。
 * 真正的防线是「运营账号不兼任卖家/货代」与「A 级操作二次验证」（doc/49 §三）。
 */
export const ROLES = [
  { key: 'tenant', label: '卖家', hint: '店铺 · 定价 · 订单 · 资金' },
  { key: 'forwarder', label: '货代', hint: '收货 · 称重 · 打包 · 交运' },
  { key: 'operator', label: '平台运营', hint: '充值确认 · 租户管理' },
] as const

export type RoleKey = (typeof ROLES)[number]['key']

export async function login(request: {
  /** 🔴 决定打哪条路径。⛔ 绝不放进请求体 —— 那等于让调用方自选身份 */
  role: RoleKey
  phone: string
  password: string
  /** 首次不传；收到 99404 后带其中一个再发一次 */
  identityId?: string
  /** 滑块验证码（失败≥3次后必填） */
  captcha?: CaptchaInput
}): Promise<LoginOutcome> {
  const env = await postEnvelope<TokenPairWire | { identities: IdentityOptionWire[] }>(
    `/auth/v1/${request.role}/login`,
    {
      phone: request.phone,
      password: request.password,
      identityId: request.identityId ?? null,
      audience: 'WEB',
      captcha: request.captcha ?? null,
    },
  )

  if (env.code === 0 && env.data) {
    const p = tokenPairOf(env.data as TokenPairWire)
    const session: SellerSession = {
      userId: p.userId,
      subjectType: p.subjectType,
      tenantId: p.tenantId,
      subjectId: p.subjectId,
      mustChangePassword: p.mustChangePassword,
    }
    // 管理后台与内嵌货代共用本会话，只有一个刷新令牌轮换入口。
    saveTokens({ ...p, session })
    return {
      kind: 'session',
      session,
      raw: {
        accessToken: p.accessToken,
        accessExpiresIn: p.accessExpiresIn,
        refreshToken: p.refreshToken,
        refreshExpiresIn: p.refreshExpiresIn,
      },
    }
  }

  if (env.code === 99404) {
    const d = env.data as { identities?: IdentityOptionWire[] } | null
    // ⚠️ 后端说了会给列表；给不出来时⛔不要静默当成普通失败，说清楚
    return { kind: 'choose-identity', identities: (d?.identities ?? []).map(identityOptionOf) }
  }

  // ⛔ 不自己编文案 —— 后端的 message 是斟酌过的（erp-web/README §四）
  throw new LoginError(env.code, env.message, TERMINAL_CODES.has(env.code))
}

export async function logout(): Promise<void> {
  const { refreshTokenOrNull, clearTokens } = await import('./tokens')
  const rt = refreshTokenOrNull()
  try {
    if (rt) await postEnvelope('/auth/v1/logout', { refreshToken: rt })
  } catch {
    // ⚠️ 登出失败不该把人卡在登录态里 —— 本地一定要清掉。
    //    后端那边的会话会自己过期（refresh 有有效期）。
  } finally {
    clearTokens()
  }
}

/**
 * 自助改密 —— 调**已有的** `POST /auth/v1/password`（不在三端前缀闸门之下，三端共用）。
 *
 * <h2>🔴🔴 改完该用户的**全部会话（含当前这个）一律作废** —— 后端 AuthService.changeOwnPassword 做的</h2>
 * 这是刻意的：改密码最常见的动机就是「怀疑密码泄露了」，不撤销的话泄露者手里的会话照旧有效。
 * ⇒ 调用方在成功之后**必须**清掉本地令牌并跳登录页（与登出同处置），
 *    ⛔ 不能让用户以为「改完了，继续用」—— 那张令牌已经被后端作废，下一个请求就是 401。
 *
 * <p>🔴 必须带旧密码：一台被临时借用的、已登录的电脑不足以证明操作者就是账号主人。
 *
 * <p>⚠️ 这里走 `postEnvelope` 而不是 shared 的 http 客户端，与 login/logout 同口径：
 * 改密是 auth 域，且我们要原样拿到后端的 `message`（shared 客户端在 code≠0 时抛 ApiError，
 * 虽然也能拿到 message，但这里保持与同文件其它函数一致）。
 *
 * @returns 成功（code=0）时为 void；失败抛 `LoginError`（沿用登录的错误类型）
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const env = await postEnvelope<null>('/auth/v1/password', {
    currentPassword,
    newPassword,
  }, bearerHeader())
  if (env.code !== 0) {
    // ⛔ 不自己编文案 —— 后端的 message 是斟酌过的（如「当前密码不正确」「新密码不能与当前密码相同」）
    throw new LoginError(env.code, env.message, TERMINAL_CODES.has(env.code))
  }
  // 🔴 后端已撤销全部会话 ⇒ 本地令牌必须立刻清掉，否则下一个业务请求会拿到 401 而看起来「突然掉了线」
  const { clearTokens } = await import('./tokens')
  clearTokens()
}

// ---- 滑块验证码 ----

/** 滑块验证码挑战 —— 后端返回带缺口的背景图 + 滑块块 */
export interface CaptchaChallenge {
  captchaId: string
  /** 带缺口的背景图（Base64 PNG，data:image/png;base64,...） */
  backgroundImage: string
  /** 滑块块图片（Base64 PNG） */
  sliderImage: string
  /** 滑块块的 Y 坐标（前端定位用） */
  sliderY: number
  /** 后端判定坐标所使用的原始画布宽度 */
  canvasWidth: number
  /** 原始画布高度 */
  canvasHeight: number
  /** 拼图块边长 */
  blockSize: number
}

/** 滑块验证码输入（登录时带上，后端校验拖动位置） */
export interface CaptchaInput {
  captchaId: string
  /** 用户拖动的 X 位置 */
  sliderX: number
}

/**
 * 获取滑块验证码 —— `GET /auth/v1/captcha`。
 * 🔴 公开端点，不需要登录。
 * 失败次数 ≥ 3 时前端调这个拿验证码。
 */
export async function getCaptcha(): Promise<CaptchaChallenge> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 10_000)
  let res: Response
  try {
    res = await fetch('/auth/v1/captcha', { signal: controller.signal })
  } catch {
    throw new LoginError(-1, '无法连接服务器获取验证码', false)
  } finally {
    clearTimeout(timer)
  }
  const json = (await res.json()) as Envelope<CaptchaChallenge>
  if (json.code !== 0 || !json.data) {
    throw new LoginError(json.code, json.message, false)
  }
  return json.data
}
